/**
 * BookingSummary — shared component + calculation utilities.
 * Used in both the admin editor (read-only preview) and the public proposal view.
 *
 * Pricing model:
 * - Each item with structured pricing has `amount` (number) and `pricing_mode`
 *   in {included, excluded, on_request, unavailable}. Only `included` items
 *   contribute to the total. Empty `pricing_mode` is treated as `included`
 *   when `amount > 0` (graceful default).
 * - `insurance` block has fields (not items) — same rules apply at block level.
 */
import { useMemo } from "react";
import { Euro, Wallet, AlertCircle, CheckCircle2 } from "lucide-react";

const BLOCK_LABELS = {
  flights: "Vuelos",
  hotels: "Hotel",
  transfers: "Traslados",
  restaurants: "Restaurantes",
  activities: "Actividades",
  excursions: "Excursiones",
  transport: "Transporte",
  insurance: "Seguro de viaje",
};

const PRICED_ITEM_BLOCKS = new Set([
  "flights", "hotels", "transfers", "restaurants", "activities", "excursions", "transport",
]);

function toNumber(v) {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function formatEUR(n) {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n || 0);
}

/**
 * Compute the booking summary from proposal blocks + settings + overrides.
 * Returns a plain object — pure function, safe to memoise.
 */
export function computeBookingSummary(proposal, settings) {
  const blocks = (proposal?.blocks || []).filter(
    (b) => !b.hidden && b.type !== "private_notes"
  );

  const lines = [];

  for (const block of blocks) {
    if (PRICED_ITEM_BLOCKS.has(block.type)) {
      const items = block.data?.items || [];
      let subtotal = 0;
      let countOnReq = 0;
      let countUnav = 0;
      let countExcluded = 0;
      let countIncluded = 0;
      for (const it of items) {
        const mode = it.pricing_mode || (toNumber(it.amount) > 0 ? "included" : "");
        if (mode === "on_request") countOnReq++;
        else if (mode === "unavailable") countUnav++;
        else if (mode === "excluded") countExcluded++;
        else if (mode === "included") {
          countIncluded++;
          subtotal += toNumber(it.amount);
        }
      }
      if (items.length === 0) continue;
      lines.push({
        blockType: block.type,
        label: BLOCK_LABELS[block.type] || block.title,
        amount: subtotal,
        itemCount: items.length,
        countIncluded,
        countOnReq,
        countUnav,
        countExcluded,
      });
    } else if (block.type === "insurance") {
      const data = block.data || {};
      const mode = data.pricing_mode || (toNumber(data.amount) > 0 ? "included" : "");
      const amount = mode === "included" ? toNumber(data.amount) : 0;
      if (!data.company && !data.price && amount === 0 && !mode) continue;
      lines.push({
        blockType: "insurance",
        label: BLOCK_LABELS.insurance,
        amount,
        itemCount: 1,
        countIncluded: mode === "included" ? 1 : 0,
        countOnReq: mode === "on_request" ? 1 : 0,
        countUnav: mode === "unavailable" ? 1 : 0,
        countExcluded: mode === "excluded" ? 1 : 0,
      });
    }
  }

  const baseTotal = lines.reduce((s, l) => s + (l.amount || 0), 0);

  // Settings + overrides
  const s = settings || {};
  const ivaPct =
    proposal?.iva_pct_override != null ? Number(proposal.iva_pct_override) :
    s.iva_pct != null ? Number(s.iva_pct) : 21;
  const pricesIncludeIva =
    proposal?.prices_include_iva_override != null ? !!proposal.prices_include_iva_override :
    s.prices_include_iva != null ? !!s.prices_include_iva : true;
  const mgmtFeeKind =
    proposal?.mgmt_fee_kind_override || s.mgmt_fee_kind || "none";
  const mgmtFeeValue =
    proposal?.mgmt_fee_value_override != null ? toNumber(proposal.mgmt_fee_value_override) :
    toNumber(s.mgmt_fee_value);
  const depositKind =
    proposal?.deposit_kind_override || s.deposit_kind || "none";
  const depositValue =
    proposal?.deposit_value_override != null ? toNumber(proposal.deposit_value_override) :
    toNumber(s.deposit_value);

  // Management fee — applied on top of baseTotal
  let mgmtFee = 0;
  if (mgmtFeeKind === "pct") mgmtFee = (baseTotal * mgmtFeeValue) / 100;
  else if (mgmtFeeKind === "fixed") mgmtFee = mgmtFeeValue;

  // IVA — if prices include IVA, it's informational. Otherwise added on top.
  const preTax = baseTotal + mgmtFee;
  const ivaAmount = pricesIncludeIva
    ? (preTax - preTax / (1 + ivaPct / 100))
    : (preTax * ivaPct) / 100;
  const total = pricesIncludeIva ? preTax : preTax + ivaAmount;

  // Deposit — applied to total
  let deposit = 0;
  if (depositKind === "pct") deposit = (total * depositValue) / 100;
  else if (depositKind === "fixed") deposit = depositValue;

  return {
    lines,
    baseTotal,
    mgmtFee,
    mgmtFeeKind,
    mgmtFeeValue,
    ivaPct,
    ivaAmount,
    pricesIncludeIva,
    total,
    deposit,
    depositKind,
    depositValue,
    currency: "EUR",
  };
}

/**
 * Display component. `variant` controls styling (light = admin preview, dark = public).
 */
export default function BookingSummary({ proposal, settings, variant = "light", footer = null }) {
  const sum = useMemo(() => computeBookingSummary(proposal, settings), [proposal, settings]);

  const isDark = variant === "dark";

  const wrapCls = isDark
    ? "bg-[#0A192F] text-white p-7 lg:p-10 border border-[#E8C97A]/20"
    : "bg-white text-[#0A192F] p-6 lg:p-8 border border-[#16335A]/15";

  const lineCls = isDark
    ? "flex items-baseline justify-between py-3 border-b border-white/10 last:border-0 text-sm"
    : "flex items-baseline justify-between py-3 border-b border-[#16335A]/10 last:border-0 text-sm";

  const labelCls = isDark ? "text-white/90" : "text-[#0A192F]";
  const amountCls = isDark ? "text-[#E8C97A] font-semibold" : "text-[#16335A] font-semibold";
  const mutedCls = isDark ? "text-white/50 text-xs" : "text-[#3A506B]/70 text-xs";

  const hasLines = sum.lines.length > 0;

  return (
    <section
      className={wrapCls}
      style={{ borderRadius: "2px" }}
      data-testid="booking-summary"
    >
      <div className="flex items-center gap-3 mb-4">
        <Wallet size={18} className={isDark ? "text-[#E8C97A]" : "text-[#16335A]"} />
        <h2 className={`font-display text-2xl ${isDark ? "text-white" : "text-[#0A192F]"}`}>
          Resumen de Reserva
        </h2>
      </div>

      {!hasLines && (
        <p className={`${mutedCls} italic`}>
          Añade importes a los elementos del viaje para ver el desglose automático.
        </p>
      )}

      {hasLines && (
        <div className="mt-2">
          {sum.lines.map((l) => (
            <div key={l.blockType} className={lineCls} data-testid={`summary-line-${l.blockType}`}>
              <div className="flex-1">
                <div className={labelCls}>{l.label}</div>
                <div className={mutedCls}>
                  {l.countIncluded} {l.countIncluded === 1 ? "concepto" : "conceptos"} en total
                  {l.countOnReq > 0 && ` · ${l.countOnReq} bajo petición`}
                  {l.countUnav > 0 && ` · ${l.countUnav} no disponible`}
                  {l.countExcluded > 0 && ` · ${l.countExcluded} excluido`}
                </div>
              </div>
              <div className={amountCls}>{l.amount > 0 ? formatEUR(l.amount) : "—"}</div>
            </div>
          ))}

          {sum.mgmtFee > 0 && (
            <div className={lineCls} data-testid="summary-line-fee">
              <div className={labelCls}>
                Gastos de gestión
                {sum.mgmtFeeKind === "pct" && (
                  <span className={mutedCls}> · {sum.mgmtFeeValue}%</span>
                )}
              </div>
              <div className={amountCls}>{formatEUR(sum.mgmtFee)}</div>
            </div>
          )}

          {/* IVA — info only if included, additive if not */}
          {sum.ivaPct > 0 && (
            <div className={lineCls} data-testid="summary-line-iva">
              <div className={labelCls}>
                IVA ({sum.ivaPct}%)
                {sum.pricesIncludeIva && <span className={mutedCls}> · incluido</span>}
              </div>
              <div className={amountCls}>
                {sum.pricesIncludeIva ? `(${formatEUR(sum.ivaAmount)})` : formatEUR(sum.ivaAmount)}
              </div>
            </div>
          )}

          {/* TOTAL */}
          <div
            className={
              "mt-4 pt-4 flex items-baseline justify-between " +
              (isDark ? "border-t-2 border-[#E8C97A]/50" : "border-t-2 border-[#16335A]")
            }
            data-testid="summary-total"
          >
            <div className={`font-display text-xl ${isDark ? "text-white" : "text-[#0A192F]"}`}>
              Total del viaje
            </div>
            <div className={`font-display text-3xl lg:text-4xl ${isDark ? "text-[#E8C97A]" : "text-[#16335A]"}`}>
              {formatEUR(sum.total)}
            </div>
          </div>

          {sum.deposit > 0 && (
            <div
              className={
                "mt-3 flex items-baseline justify-between text-sm " +
                (isDark ? "text-white/80" : "text-[#3A506B]")
              }
              data-testid="summary-deposit"
            >
              <div className="inline-flex items-center gap-2">
                <Euro size={13} />
                Señal para confirmar la reserva
                {sum.depositKind === "pct" && (
                  <span className={mutedCls}> · {sum.depositValue}% del total</span>
                )}
              </div>
              <div className={isDark ? "text-[#E8C97A] font-semibold" : "text-[#16335A] font-semibold"}>
                {formatEUR(sum.deposit)}
              </div>
            </div>
          )}
        </div>
      )}

      {footer && <div className="mt-6">{footer}</div>}
    </section>
  );
}

export { formatEUR };
